import { useState } from 'react';
import AdminTables from '@components/admin/AdminTables';
import Modal from '@components/common/Modal';
import { useBookings } from '@hooks/useBookings';

const AdminBookings = () => {
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  
  const { bookings, loading, updateBookingStatus } = useBookings();

  const columns = [
    { key: 'id', label: 'Booking ID' },
    { 
      key: 'date', 
      label: 'Date',
      render: (value) => new Date(value).toLocaleDateString()
    },
    { key: 'time', label: 'Time' },
    { key: 'serviceName', label: 'Service' },
    { key: 'customerName', label: 'Customer' },
    { key: 'postcode', label: 'Postcode' },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
          value === 'completed' ? 'bg-green-100 text-green-600' :
          value === 'confirmed' ? 'bg-blue-100 text-blue-600' :
          value === 'pending' ? 'bg-yellow-100 text-yellow-600' :
          'bg-red-100 text-red-600'
        }`}>
          {value.charAt(0).toUpperCase() + value.slice(1)}
        </span>
      )
    },
  ];

  const handleEdit = (booking) => {
    setSelectedBooking(booking);
    setIsDetailModalOpen(true);
  };

  const handleDelete = (booking) => {
    if (window.confirm(`Are you sure you want to delete booking ${booking.id}?`)) {
      console.log('Delete booking:', booking.id);
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await updateBookingStatus(bookingId, newStatus);
    } catch (error) {
      console.error('Failed to update booking status:', error);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Bookings</h1>
        <p className="text-gray-600 mt-2">Manage service bookings and appointments</p>
      </div>

      <AdminTables
        columns={columns}
        data={bookings}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isLoading={loading}
      />

      {/* Booking Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedBooking(null);
        }}
        title={`Booking ${selectedBooking?.id}`}
      >
        {selectedBooking && (
          <div className="space-y-6">
            {/* Service Info */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Service Information</h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p><span className="font-medium">Service:</span> {selectedBooking.serviceName}</p>
                <p><span className="font-medium">Date:</span> {new Date(selectedBooking.date).toLocaleDateString()}</p>
                <p><span className="font-medium">Time:</span> {selectedBooking.time}</p>
              </div>
            </div>

            {/* Customer Info */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Customer Information</h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p><span className="font-medium">Name:</span> {selectedBooking.customerName}</p>
                <p><span className="font-medium">Email:</span> {selectedBooking.email}</p>
                <p><span className="font-medium">Phone:</span> {selectedBooking.phone}</p>
                <p><span className="font-medium">Postcode:</span> {selectedBooking.postcode}</p>
              </div>
            </div>

            {/* Status Update */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Update Status</h3>
              <select
                value={selectedBooking.status}
                onChange={(e) => handleStatusChange(selectedBooking.id, e.target.value)}
                className="input"
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminBookings;

